import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../../contexts/OrganizationContext';
import { fetchMissions } from '../../services/missionService';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, TextField, Button, Select, MenuItem, FormControl, InputLabel } from '@mui/material';

const MissionSummary = () => {
    const navigate = useNavigate();

    const { selectedOrg, updateSelectedOrg } = useOrganization();
    const [missions, setMissions] = useState([]);
    const [filteredMissions, setFilteredMissions] = useState([]);
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [filter, setFilter] = useState('all');

    // Fetch missions on load
    useEffect(() => {
        const getMissions = async () => {
            try {
                const data = await fetchMissions(selectedOrg); // Ensure this fetches all missions
                setMissions(data);
                setFilteredMissions(data);
            } catch (err) {
                console.error('Failed to fetch missions:', err);
            }
        };
        getMissions();
    }, []);

    // Filter missions based on date range and status
    useEffect(() => {
        const filterMissions = () => {
            let filtered = missions;

            // Filter by date range
            if (dateRange.start && dateRange.end) {
                const startDate = new Date(dateRange.start);
                const endDate = new Date(dateRange.end);
                filtered = filtered.filter((mission) => {
                    const missionDate = new Date(mission.createdAt);
                    return missionDate >= startDate && missionDate <= endDate;
                });
            }

            // Filter by status
            if (filter !== 'all') {
                //                filtered = filtered.filter((mission) => mission.status === filter);
            }

            // Filter by status
            if (filter !== 'all') {
                filtered = filtered.filter((mission) => {
                    switch (filter) {
                        case 'Completed':
                            return mission.completedAt; // Completed if completedAt is not empty
                        case 'In Progress':
                            return mission.startedAt && !mission.completedAt; // Started but not completed
                        case 'Not Started':
                            return !mission.startedAt; // Not started if startedAt is empty
                        default:
                            return true; // Default case (fallback)
                    }
                });
            }

            setFilteredMissions(filtered);
        };

        filterMissions();
    }, [dateRange, filter, missions]);

    // Calculate Summary Statistics
    const totalMissions = missions.length;
    const completedMissions = missions.filter((mission) => mission.completedAt).length;
    const inProgressMissions = missions.filter((mission) => mission.startedAt && !mission.completedAt).length;
    const notStartedMissions = missions.filter((mission) => !mission.startedAt).length;

    const handleBackToReports = () => {
        navigate('/reports');
    };

    return (
        <Box sx={{ padding: '16px' }}>
            {/* Back to Reports Button */}
            <Button
                variant="text"
                onClick={handleBackToReports}
                startIcon={<ArrowBackIcon />}
                sx={{
                    marginBottom: '16px',
                    color: '#2c3e50',
                    textTransform: 'none',
                    fontWeight: 400,
                    '&:hover': {
                        backgroundColor: '#f0f0f0',
                    },
                }}
            >
                Reports
            </Button>

            <Typography variant="h4" gutterBottom>
                Mission Summary Report
            </Typography>

            {/* Summary Stats */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                <Box>
                    <Typography variant="h6">Total Missions</Typography>
                    <Typography variant="h5">{totalMissions}</Typography>
                </Box>
                <Box>
                    <Typography variant="h6">Completed Missions</Typography>
                    <Typography variant="h5">{completedMissions}</Typography>
                </Box>
                <Box>
                    <Typography variant="h6">In Progress</Typography>
                    <Typography variant="h5">{inProgressMissions}</Typography>
                </Box>
                <Box>
                    <Typography variant="h6">Not Started</Typography>
                    <Typography variant="h5">{notStartedMissions}</Typography>
                </Box>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <TextField
                    label="Start Date"
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ minWidth: 140 }}

                />
                <TextField
                    label="End Date"
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ minWidth: 140 }}
                />
                <TextField
                    select
                    label="Status"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    variant="outlined"
                    sx={{ minWidth: 120 }}
                >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="Not Started">Not Started</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                </TextField>
                <Button
                    variant="contained"
                    onClick={() => {
                        setDateRange({ start: '', end: '' });
                        setFilter('all'); // Reset the status filter to "All"
                    }}
                >
                    Clear Filters
                </Button>
            </Box>

            {/* Mission Table */}
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Mission Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created At</TableCell>
                        <TableCell>Completed At</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredMissions.map((mission) => (
                        <TableRow key={mission.missionGuid}>
                            <TableCell>{mission.missionName || 'Unnamed Mission'}</TableCell>
                            <TableCell>{mission.status}</TableCell>
                            <TableCell>{new Date(mission.createdAt).toLocaleDateString()}</TableCell>
                            <TableCell>
                                {mission.completedAt
                                    ? new Date(mission.completedAt).toLocaleDateString()
                                    : 'N/A'}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
};

export default MissionSummary;